/**
 * @zendir/ui - GroundTrackMap Component (Enhanced)
 *
 * Full-featured 2D world map showing spacecraft ground tracks, current positions,
 * ground station locations, coverage footprints, satellite icons, and day/night terminator.
 *
 * Inspired by Astro UXDS TT&C Monitor / GRM Dashboard ground map patterns:
 * - Satellite icons with status-colored indicators (not just dots)
 * - Ground station antenna icons with coverage circles
 * - Sensor/antenna footprint cone projections
 * - Future orbit tracks (dashed) vs past tracks (solid)
 * - Day/night terminator
 * - Multi-satellite support
 * - Interactive hover tooltips
 * - AOS/LOS markers on track
 * - Legend with satellite/station counts
 *
 * Astro UX Compliance:
 * - Theme-integrated via useTheme()
 * - Uses official Astro status colors from theme tokens
 * - Uses Astro monitoring icon patterns
 * - 14pt minimum for labels (AstroUXDS guideline)
 * - Consistent with Astro data visualization patterns
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTheme } from "../theme";
import { AstroIcon } from "../core/display/AstroIcon";
import type { GroundTrackPoint, GroundStation } from "../types";
import type { GroundTrackMapLeafletProps } from "./GroundTrackMapLeaflet";

// =============================================================================
// Types
// =============================================================================

export interface SatelliteTrack {
  /** Unique satellite identifier */
  id: string;
  /** Satellite display name */
  name: string;
  /** Ground track points */
  groundTrack: GroundTrackPoint[];
  /** Track color */
  color?: string;
  /** Satellite status for icon coloring */
  status?: "normal" | "caution" | "serious" | "critical" | "standby" | "off";
  /** Access mask (true = in contact) */
  accessMask?: boolean[];
  /** Show this satellite's sensor footprint */
  showFootprint?: boolean;
  /** Footprint radius in degrees (sensor cone half-angle on ground) */
  footprintRadius?: number;
  /** Show future (predicted) track as dashed line */
  futureTrackIndex?: number;
  /** AOS/LOS markers */
  passMarkers?: Array<{
    type: "aos" | "los";
    latitude: number;
    longitude: number;
    label?: string;
  }>;
}

export interface GroundStationEnhanced extends GroundStation {
  /** Station status */
  status?: "normal" | "caution" | "serious" | "critical" | "standby" | "off";
  /** Show coverage circle */
  showCoverage?: boolean;
  /** Coverage radius in degrees (visibility horizon) */
  coverageRadius?: number;
  /** Station type (affects icon) */
  type?: "dish" | "phased-array" | "omni" | "relay";
}

/**
 * SRO-compatible team path format (space-range-operator CustomMap data shape).
 * Provide this as an alternative to `satellites` for plug-and-play compatibility.
 */
export interface TeamPath {
  /** Team/satellite name */
  name: string;
  /** Track color */
  color?: string;
  /** Ordered position array */
  positions: Array<{
    lat: number;
    lng: number;
    time?: number;
    altitude?: number | null;
  }>;
}

/** SRO-compatible ground station format */
export interface SROGroundStation {
  name: string;
  latitude: number;
  longitude: number;
  altitude?: number | null;
}

/**
 * User-placed point-of-interest pin on the map.
 * Designed for collaborative scenarios where operators share annotated locations
 * across terminals in real time (e.g. targets, waypoints, hazard markers).
 */
export interface MapPin {
  /** Unique identifier (used as key and for update/remove callbacks) */
  id: string;
  /** Latitude in degrees (−90 … 90) */
  latitude: number;
  /** Longitude in degrees (−180 … 180) */
  longitude: number;
  /** Short label displayed next to the pin (e.g. "Target Alpha") */
  label?: string;
  /** Pin color — any CSS color string. Defaults to accent primary. */
  color?: string;
  /** Optional description shown in tooltip on hover */
  description?: string;
  /** Who placed the pin (display-only; not enforced) */
  createdBy?: string;
}

/**
 * A point light source rendered on the night side of the map.
 * Use this to display city lights, base locations, RF emitters,
 * or any glow that should only be visible after sunset.
 *
 * Light sources are masked by the terminator — they fade in
 * through the twilight gradient and reach full brightness in
 * deep night, matching real-world light-pollution behavior.
 */
export interface LightSource {
  /** Latitude in degrees (−90 … 90) */
  latitude: number;
  /** Longitude in degrees (−180 … 180) */
  longitude: number;
  /** Glow radius in pixels (default 4). Larger values create wider halos. */
  radius?: number;
  /** Glow intensity 0–1 (default 0.8). Controls the peak alpha of the light dot. */
  intensity?: number;
  /** Light color — any CSS color string (default '#ffeedd', warm white). */
  color?: string;
  /** Optional label shown on hover (Leaflet) or in tooltip (Canvas). */
  label?: string;
}

// `MapLayerDef` is a backward-compat alias for the shared `LayerDef` —
// see `react/types.ts`. Re-exported here so existing consumers keep working.
export type { MapLayerDef } from "../types";
import type { LayerDef as MapLayerDef } from "../types";

export interface GroundTrackMapProps {
  /** Single satellite ground track (legacy API) */
  groundTrack?: GroundTrackPoint[];
  /** Multiple satellite tracks */
  satellites?: SatelliteTrack[];
  /** Ground stations to display */
  groundStations?: (GroundStation | GroundStationEnhanced | SROGroundStation)[];
  /** Access mask for single-satellite mode (legacy API) */
  accessMask?: boolean[];

  // --- SRO-compatible alternate API (space-range-operator plug-and-play) ---
  /** Team path data (SRO CustomMap format) — auto-converts to satellites internally */
  teamPaths?: TeamPath[];

  // --- Display options ---
  /** Show day/night terminator */
  showTerminator?: boolean;
  /** Override the time used for the day/night terminator calculation.
   *  Defaults to wall-clock `new Date()` when omitted.
   *  Pass a simulation/mission UTC to keep the terminator in sync with a sim clock. */
  terminatorTime?: Date;
  /** Show grid lines */
  showGrid?: boolean;
  /** Show legend */
  showLegend?: boolean;
  /** Show equator highlight */
  showEquator?: boolean;
  /** Show recenter button (SRO compat) */
  showRecenterButton?: boolean;
  /** Show a toggle button to switch between Dark and Satellite tile styles. Default true for Leaflet. */
  showMapStyleToggle?: boolean;

  // --- State ---
  /** Whether data is still loading (SRO compat) */
  isLoading?: boolean;
  /** Message when there is no data (SRO compat) */
  emptyMessage?: string;

  // --- Sizing ---
  /** Chart width (default: 100%) */
  width?: number | string;
  /** Chart height — number for fixed px, or CSS string like '100%' to fill a flex parent */
  height?: number | string;
  /** Minimum height CSS string (SRO compat, e.g. "400px") */
  minHeight?: string;

  // --- Centering ---
  /** Default center [lat, lon] (SRO compat) */
  defaultCenter?: [number, number];
  /** Default zoom level 1-10 (SRO compat, affects initial scale) */
  defaultZoom?: number;

  /** Custom className */
  className?: string;
  /** Click handler for satellites */
  onSatelliteClick?: (satelliteId: string) => void;
  /** Click handler for ground stations */
  onStationClick?: (stationId: string) => void;

  /** Map backend: 'leaflet' (default, requires leaflet peer) or 'canvas' */
  mapProvider?: "leaflet" | "canvas";
  /** Tile URL for Leaflet (default: CartoDB dark). Ignored when mapProvider is 'canvas'. */
  tileUrl?: string;

  // --- Day/Night Tile Masking (Leaflet only) ---
  /**
   * URL template for a "night" tile layer (e.g. NASA Black Marble, VIIRS city lights).
   * When provided, this layer is rendered beneath the terminator overlay so that it
   * appears only on the night side of the map — creating a realistic day/night transition.
   * The day tiles remain visible on the sunlit side.
   *
   * Requires `showTerminator` to be enabled. Leaflet backend only.
   *
   * Example: `'https://map1.vis.earthdata.nasa.gov/wmts-webmerc/VIIRS_CityLights_2012/...'`
   */
  nightTileUrl?: string;

  // --- Dynamic Light Sources ---
  /**
   * Array of point light sources rendered on the night side of the map.
   * Each light appears as a soft glow dot that is masked by the terminator —
   * invisible in daylight, fading in through twilight, full brightness at night.
   *
   * Use cases: city lights, base indicators, RF emitters, targets, population centers.
   */
  lightSources?: LightSource[];

  // --- Collaborative Pins (Points of Interest) ---
  /** Array of user-placed pins displayed on the map */
  pins?: MapPin[];
  /**
   * Allow operators to place new pins by clicking the map.
   * When true, a single-click on an empty area opens a pin creation flow.
   * Requires `onPinAdd` to be set. Default false.
   */
  pinsEditable?: boolean;
  /** Called when the user places a new pin (provides lat/lon; consumer assigns id and persists) */
  onPinAdd?: (pin: Omit<MapPin, "id">) => void;
  /** Called when the user edits an existing pin (label, color, description change) */
  onPinUpdate?: (pin: MapPin) => void;
  /** Called when the user removes a pin */
  onPinRemove?: (pinId: string) => void;

  // --- Layers Panel ---
  /**
   * Custom overlay layers shown in the Layers panel dropdown.
   * Each layer gets a toggle switch. The GroundTrackMap does NOT render
   * the layer content — it only provides the toggle UI.
   * Listen to `onLayerChange` for state updates and render your own overlays.
   */
  customLayers?: MapLayerDef[];
  /**
   * Called when any layer is toggled (built-in or custom).
   * `layerId` is one of: 'terminator', 'grid', or a custom layer id.
   * `enabled` is the new toggle state.
   */
  onLayerChange?: (layerId: string, enabled: boolean) => void;

  /**
   * Show sun and moon position markers on the map.
   * Requires `showTerminator`. Default: true when terminator is enabled.
   */
  showCelestialMarkers?: boolean;
}

// =============================================================================
// Constants — aligned with official Astro UXDS status colors
// These MUST match the STATUS_COLORS in unified/theme.ts and ThemeProvider
// =============================================================================

const STATUS_COLOR_MAP: Record<string, string> = {
  normal: "#56f000", // Green — Astro UXDS "Normal"
  standby: "#2dccff", // Cyan — Astro UXDS "Standby"
  caution: "#fce83a", // Yellow — Astro UXDS "Caution"
  serious: "#ffb302", // Orange — Astro UXDS "Serious"
  critical: "#ff3838", // Red — Astro UXDS "Critical"
  off: "#a4abb6", // Grey — Astro UXDS "Off"
};

const DEFAULT_TRACK_COLORS = [
  "#2dccff",
  "#3E3CFF",
  "#9D70FF",
  "#56f000",
  "#fce83a",
  "#ff7849",
  "#2dd4bf",
  "#ff3838",
];

// Earth radius (km) — shared with the 3D backends to avoid drift.
import { EARTH_RADIUS_KM } from "../3d/ZenSpace3DUtils";
const _DEG_PER_KM = 1 / 111.32; // Approximate degrees per km at equator

// Higher-quality world map coastline paths (Mercator-compatible)
const WORLD_MAP_PATHS = [
  // North America
  "M-168,70 L-168,60 L-145,63 L-140,58 L-130,55 L-125,49 L-122,48 L-117,33 L-115,28 L-105,22 L-97,18 L-88,20 L-82,25 L-82,30 L-80,32 L-67,47 L-60,46 L-55,52 L-56,60 L-65,70 L-85,73 L-100,72 L-130,70 L-168,70",
  // South America
  "M-82,12 L-77,8 L-73,11 L-65,5 L-60,5 L-50,0 L-45,-5 L-43,-10 L-39,-15 L-38,-22 L-40,-25 L-43,-30 L-48,-33 L-50,-40 L-55,-48 L-57,-52 L-68,-55 L-73,-50 L-78,-42 L-80,-20 L-78,-5 L-82,12",
  // Europe + Africa
  "M-10,72 L-5,60 L-5,48 L0,44 L3,44 L5,48 L8,44 L3,37 L-5,35 L-17,28 L-17,15 L-8,10 L-4,5 L5,5 L10,2 L15,-3 L22,-12 L28,-22 L33,-30 L30,-35 L22,-35 L18,-27 L12,-20 L8,-10 L3,0 L-5,10 L-12,15 L-15,20 L-13,32 L-10,38 L0,48 L8,55 L15,58 L20,60 L25,65 L30,70 L-10,72",
  // Asia
  "M30,70 L40,65 L50,60 L60,55 L70,50 L80,45 L90,45 L100,40 L105,35 L110,30 L115,25 L120,30 L125,35 L130,40 L135,45 L140,50 L145,55 L150,60 L155,65 L160,68 L170,70 L180,70 L180,72 L30,72 L30,70",
  // Australia
  "M112,-12 L118,-14 L125,-15 L132,-13 L138,-15 L143,-17 L148,-20 L152,-23 L153,-28 L150,-33 L147,-37 L140,-38 L134,-36 L130,-33 L126,-30 L120,-30 L116,-28 L114,-24 L113,-18 L112,-12",
  // Antarctica
  "M-180,-72 L180,-72 L180,-90 L-180,-90 Z",
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate ground station visibility radius in degrees.
 * Uses spherical Earth geometry: for a station with minimum elevation angle `elDeg`
 * and a satellite at altitude `altKm`, the half-angle subtended at Earth center is:
 *   ρ = acos(R / (R + h)) − el_rad
 * Then the ground-range in degrees ≈ ρ * (180/π).
 * If no altitude context, coverageRadius prop is used directly (in degrees).
 */
function _visibilityRadiusDeg(
  altitudeKm: number = 500,
  minElevationDeg: number = 5,
): number {
  const elRad = (minElevationDeg * Math.PI) / 180;
  const rho =
    Math.acos(EARTH_RADIUS_KM / (EARTH_RADIUS_KM + altitudeKm)) - elRad;
  return Math.max(0, (rho * 180) / Math.PI);
}

/**
 * Draw a ground-circle footprint using great-circle math.
 * Given a center lat/lon and an angular radius (in degrees measured along
 * Earth's surface), compute N points around the circle on the sphere, then
 * project each to equirectangular pixel coordinates.
 *
 * This produces a circle that is geographically correct AND looks natural
 * on the map — at high latitudes the circle is wider in longitude because
 * that's how equirectangular projection works, but it's capped by reality
 * rather than a simple 1/cos blowup.
 */
function drawCoverageEllipse(
  ctx: CanvasRenderingContext2D,
  centerLat: number,
  centerLon: number,
  radiusDeg: number,
  W: number,
  H: number,
  lonToX: (lon: number, w: number) => number,
  latToY: (lat: number, h: number) => number,
  numSegments: number = 72,
): void {
  const lat0 = (centerLat * Math.PI) / 180;
  const lon0 = (centerLon * Math.PI) / 180;
  const d = (radiusDeg * Math.PI) / 180; // angular radius in radians

  // Compute the center pixel once — all points are placed relative to this
  // so the circle never jumps across the date-line seam.
  const cx = lonToX(centerLon, W);
  const cy = latToY(centerLat, H);
  const pxPerDegLon = W / 360;
  const pxPerDegLat = H / 180;

  ctx.beginPath();
  for (let i = 0; i <= numSegments; i++) {
    const bearing = (i / numSegments) * 2 * Math.PI;

    // Great-circle destination formula
    const sinLat =
      Math.sin(lat0) * Math.cos(d) +
      Math.cos(lat0) * Math.sin(d) * Math.cos(bearing);
    const lat = Math.asin(Math.max(-1, Math.min(1, sinLat)));
    const lon =
      lon0 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat0),
        Math.cos(d) - Math.sin(lat0) * sinLat,
      );

    const pLatDeg = (lat * 180) / Math.PI;
    const pLonDeg = (lon * 180) / Math.PI;

    // Pixel offset from center — no normalization, so no date-line jump
    const dLon = pLonDeg - centerLon;
    const dLat = pLatDeg - centerLat;
    const px = cx + dLon * pxPerDegLon;
    const py = cy - dLat * pxPerDegLat; // Y inverted (lat goes up, pixels go down)

    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}
//sz ee*s

/** Parse a CSS hex color to [r, g, b]. Falls back to warm white. */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }
  if (h.length >= 6) {
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  return [255, 238, 221]; // fallback warm white
}

/**
 * Calculate solar terminator points for a given date and solar depression angle.
 *
 * `depressionDeg` offsets the zenith angle to produce twilight boundaries:
 *   - 0° = geometric sunset/sunrise
 *   - 6° = civil twilight (IAU/USNO)
 *   - 12° = nautical twilight
 *   - 18° = astronomical twilight
 */

/** Calculate the sub-solar point (latitude, longitude) for a given UTC Date. */
function calculateSubSolarPoint(date: Date): [number, number] {
  const dayOfYear = Math.floor(
    (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86400000,
  );
  const declination =
    -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
  const B = ((2 * Math.PI) / 365) * (dayOfYear - 81);
  const eotMinutes =
    9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const utcHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  const solarHours = utcHours + eotMinutes / 60;
  const lon = -(solarHours - 12) * 15;
  return [declination, ((lon + 540) % 360) - 180];
}

/** Calculate the approximate sub-lunar point for a given UTC Date (low-precision). */
function calculateSubLunarPoint(date: Date): [number, number] {
  const J2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const d = (date.getTime() - J2000) / 86400000;
  const DEG = Math.PI / 180;
  const L = (218.316 + 13.176396 * d) % 360;
  const M = (134.963 + 13.064993 * d) % 360;
  const F = (93.272 + 13.22935 * d) % 360;
  const lon_ecl = L + 6.289 * Math.sin(M * DEG);
  const lat_ecl = 5.128 * Math.sin(F * DEG);
  const obliquity = 23.439 - 0.00000036 * d;
  const sinRA =
    Math.sin(lon_ecl * DEG) * Math.cos(obliquity * DEG) -
    Math.tan(lat_ecl * DEG) * Math.sin(obliquity * DEG);
  const cosRA = Math.cos(lon_ecl * DEG);
  let RA = Math.atan2(sinRA, cosRA) / DEG;
  if (RA < 0) RA += 360;
  const dec =
    Math.asin(
      Math.sin(lat_ecl * DEG) * Math.cos(obliquity * DEG) +
        Math.cos(lat_ecl * DEG) *
          Math.sin(obliquity * DEG) *
          Math.sin(lon_ecl * DEG),
    ) / DEG;
  const GMST = (280.46061837 + 360.98564736629 * d) % 360;
  let geoLon = RA - GMST;
  geoLon = ((geoLon + 540) % 360) - 180;
  return [dec, geoLon];
}

function calculateTerminatorContinuous(
  date: Date,
  depressionDeg: number = 0,
  numPoints: number = 360,
): { sunset: Array<[number, number]>; sunrise: Array<[number, number]> } {
  const dayOfYear = Math.floor(
    (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86400000,
  );
  const declination =
    -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
  const decRad = (declination * Math.PI) / 180;

  // Equation of Time correction (minutes) for more accurate solar position
  const B = ((2 * Math.PI) / 365) * (dayOfYear - 81);
  const eotMinutes =
    9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);

  // Sub-solar longitude: use full UTC time (hours + minutes + seconds) plus EoT
  // Sub-solar longitude: where the sun is directly overhead.
  const utcHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  const solarHours = utcHours + eotMinutes / 60;
  const subSolarLon = -(solarHours - 12) * 15;
  const depRad = (depressionDeg * Math.PI) / 180;

  const sunset: Array<[number, number]> = [];
  const sunrise: Array<[number, number]> = [];

  for (let i = 0; i <= numPoints; i++) {
    const lat = -89.999 + (i / numPoints) * 179.998;
    const latRad = lat * (Math.PI / 180);
    const cosH =
      -(Math.sin(depRad) + Math.sin(latRad) * Math.sin(decRad)) /
      (Math.cos(latRad) * Math.cos(decRad));

    if (cosH < -1) {
      // All day — midnight sun. No night boundary.
    } else if (cosH > 1) {
      // All night — polar night. Night spans all longitudes.
      sunset.push([lat, subSolarLon]);
      sunrise.push([lat, subSolarLon + 360]);
    } else {
      const H = (Math.acos(cosH) * 180) / Math.PI;
      // Sunset: H degrees east of sub-solar point (afternoon→evening)
      sunset.push([lat, subSolarLon + H]);
      // Sunrise: H degrees west, expressed as +360-H for continuous polygon
      sunrise.push([lat, subSolarLon + 360 - H]);
    }
  }

  return { sunset, sunrise };
}

function buildNightPolygon(terminator: {
  sunset: Array<[number, number]>;
  sunrise: Array<[number, number]>;
}): [number, number][] {
  const { sunset, sunrise } = terminator;
  if (!sunset.length || !sunrise.length) return [];

  const poly: [number, number][] = [];

  // Left edge of night (sunset): South to North
  poly.push(...sunset);

  // Right edge of night (sunrise): North to South
  poly.push(...[...sunrise].reverse());

  return poly;
}

// =============================================================================
// Component
// =============================================================================

export function GroundTrackMap({
  groundTrack,
  satellites = [],
  groundStations = [],
  accessMask,
  teamPaths,
  showTerminator = true,
  terminatorTime,
  showGrid = true,
  showLegend = true,
  showEquator = true,
  showRecenterButton = false,
  showMapStyleToggle = true,
  isLoading = false,
  emptyMessage = "No orbital data available",
  width = "100%",
  height = "100%",
  minHeight = "400px",
  defaultCenter,
  defaultZoom,
  className = "",
  onSatelliteClick,
  onStationClick,
  mapProvider = "leaflet",
  tileUrl,
  nightTileUrl,
  lightSources,
  pins,
  pinsEditable = false,
  onPinAdd,
  onPinUpdate,
  onPinRemove,
  customLayers,
  onLayerChange,
  showCelestialMarkers,
}: GroundTrackMapProps): React.ReactElement {
  const { tokens } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    type: "satellite" | "station";
    name: string;
    status: string;
    statusColor: string;
    lat: number;
    lon: number;
    alt?: number;
    // Ground station extras
    stationType?: string;
    network?: string;
    coverageRadius?: number;
    // Satellite extras
    trackColor?: string;
    hasFootprint?: boolean;
    futurePoints?: number;
    pastPoints?: number;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_viewOffset, setViewOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  // Normalize satellites: support SRO teamPaths, legacy single-track, and native API
  const allSatellites = useMemo<SatelliteTrack[]>(() => {
    // Priority 1: native satellites prop
    if (satellites.length > 0) return satellites;
    // Priority 2: SRO teamPaths format — auto-convert
    if (teamPaths && teamPaths.length > 0) {
      return teamPaths.map((tp, idx) => ({
        id: `team-${idx}`,
        name: tp.name,
        color: tp.color,
        status: "normal" as const,
        groundTrack: tp.positions
          .filter((p) => p.lat != null && p.lng != null)
          .map((p) => ({
            latitude: p.lat,
            longitude: p.lng,
            altitude: p.altitude ?? 0,
            timestamp:
              p.time != null
                ? new Date(p.time * 1000).toISOString()
                : new Date().toISOString(),
          })),
      }));
    }
    // Priority 3: legacy single-track API
    if (groundTrack && groundTrack.length > 0) {
      return [
        {
          id: "primary",
          name: "Satellite",
          groundTrack,
          status: "normal",
          accessMask,
        },
      ];
    }
    return [];
  }, [satellites, groundTrack, accessMask, teamPaths]);

  // Stable defaults for Leaflet so animated updates don't get new refs and trigger map recreation
  const leafletDefaultCenter = useMemo<[number, number]>(
    () => defaultCenter ?? [20, 0],
    [defaultCenter],
  );
  const leafletDefaultZoom = useMemo(() => defaultZoom ?? 2, [defaultZoom]);

  // Optional Leaflet map: load when mapProvider is 'leaflet'
  const [LeafletMap, setLeafletMap] =
    useState<React.ComponentType<GroundTrackMapLeafletProps> | null>(null);
  const [leafletFailed, setLeafletFailed] = useState(false);

  useEffect(() => {
    if (mapProvider !== "leaflet") return;
    import("./GroundTrackMapLeaflet")
      .then((m) => setLeafletMap(() => m.GroundTrackMapLeaflet))
      .catch((err) => {
        console.warn(
          '[GroundTrackMap] Leaflet backend failed to load. Install "leaflet" and ensure it is resolvable. Falling back to static canvas.',
          err,
        );
        setLeafletFailed(true);
      });
  }, [mapProvider]);

  // All canvas-backend hooks must run unconditionally (same hook count every render)
  // Recenter handler (canvas backend)
  const handleRecenter = useCallback(() => {
    setViewOffset({ x: 0, y: 0 });
  }, []);

  const COLORS = useMemo(
    () => ({
      background: tokens.colors.background.base,
      grid: tokens.colors.border.muted,
      text: tokens.colors.text.secondary,
      textPrimary: tokens.colors.text.primary,
      borderMuted: tokens.colors.border.muted,
      accent: tokens.colors.accent.primary,
      land: "rgba(30, 40, 58, 0.92)",
      ocean: tokens.colors.background.base,
      night: "rgba(0, 10, 40, 0.35)",
      equator: "rgba(88, 166, 255, 0.25)",
    }),
    [tokens],
  );

  const lonToX = useCallback((lon: number, w: number) => {
    let normalizedLon = lon;
    while (normalizedLon > 180) normalizedLon -= 360;
    while (normalizedLon < -180) normalizedLon += 360;
    return ((normalizedLon + 180) / 360) * w;
  }, []);

  const latToY = useCallback((lat: number, h: number) => {
    return ((90 - lat) / 180) * h;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const W = rect.width;
    // When height is a CSS string ('100%'), use the measured container height
    const H = typeof height === "number" ? height : rect.height || 400;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    // === Background ===
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, W, H);

    // === Day/Night Terminator with smooth graduated twilight ===
    // Many thin bands (every 2° of solar depression) produce a seamless
    // day→night gradient instead of hard-edged zones.
    if (showTerminator) {
      const now = terminatorTime ?? new Date();

      const BAND_STEP = 1.5;
      const MAX_DEP = 30;
      const NIGHT_ALPHA = 0.38;
      const bandSteps: Array<{ depression: number; alpha: number }> = [];
      for (let d = 0; d <= MAX_DEP; d += BAND_STEP) {
        const t = Math.min(d / 18, 1);
        const alpha =
          Math.pow(t, 1.6) * (NIGHT_ALPHA * 0.85) +
          (d > 18 ? ((d - 18) / 12) * (NIGHT_ALPHA * 0.15) : 0);
        bandSteps.push({ depression: d, alpha: Math.min(alpha, NIGHT_ALPHA) });
      }
      bandSteps.push({ depression: 90, alpha: NIGHT_ALPHA });

      let prevAlpha = 0;
      for (const zone of bandSteps) {
        const dep = Math.min(zone.depression, 89);
        const bandAlpha = zone.alpha - prevAlpha;
        if (bandAlpha < 0.002) {
          prevAlpha = zone.alpha;
          continue;
        }

        const terminator = calculateTerminatorContinuous(now, dep);
        if (terminator.sunset.length === 0) {
          prevAlpha = zone.alpha;
          continue;
        }

        const poly = buildNightPolygon(terminator);
        if (poly.length < 3) {
          prevAlpha = zone.alpha;
          continue;
        }

        ctx.fillStyle = `rgba(42, 58, 82, ${bandAlpha.toFixed(4)})`;

        // Draw the polygon (wrapping around longitude boundaries by drawing 3 copies)
        for (const offset of [-360, 0, 360]) {
          ctx.beginPath();
          for (let i = 0; i < poly.length; i++) {
            const lat = poly[i][0];
            const lon = poly[i][1] + offset;
            const x = lonToX(lon, W);
            const y = latToY(lat, H);

            // To prevent lines jumping completely across the canvas from right to left
            // if a polygon crosses the map boundary, we should probably handle it.
            // But since we draw 3 full copies (-360, 0, +360), they overlap seamlessly.
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              // If distance is huge (e.g. > W/2), it means it's wrapping around the map edge.
              // Move instead of line to avoid a horizontal streak.
              // (Actually, since we unwrap longitude continuously in calculateTerminatorContinuous,
              // there are NO jumps in longitude inside the polygon! It's one continuous shape.)
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();
          ctx.fill();
        }

        prevAlpha = zone.alpha;
      }

      // Draw a smooth terminator boundary line (0° depression)
      const terminatorEdge = calculateTerminatorContinuous(now, 0);
      if (terminatorEdge.sunset.length > 2) {
        ctx.save();
        // Soft outer glow
        ctx.strokeStyle = "rgba(160, 160, 160, 0.08)";
        ctx.lineWidth = 3;
        for (const curve of [terminatorEdge.sunset, terminatorEdge.sunrise]) {
          for (const offset of [-360, 0, 360]) {
            ctx.beginPath();
            for (let i = 0; i < curve.length; i++) {
              const x = lonToX(curve[i][1] + offset, W);
              const y = latToY(curve[i][0], H);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
        }
        // Fine core line
        ctx.strokeStyle = "rgba(168, 168, 168, 0.30)";
        ctx.lineWidth = 1;
        for (const curve of [terminatorEdge.sunset, terminatorEdge.sunrise]) {
          for (const offset of [-360, 0, 360]) {
            ctx.beginPath();
            for (let i = 0; i < curve.length; i++) {
              const x = lonToX(curve[i][1] + offset, W);
              const y = latToY(curve[i][0], H);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
        }
        ctx.restore();
      }

      // TODO: Re-enable sun/moon celestial markers once visual design is finalized.
      // Uncomment to restore: const celestialEnabled = showCelestialMarkers !== undefined ? showCelestialMarkers : true;
      const celestialEnabled = false;
      if (celestialEnabled) {
        const [sunLat, sunLon] = calculateSubSolarPoint(now);
        const sx = lonToX(sunLon, W),
          sy = latToY(sunLat, H);
        ctx.save();
        ctx.shadowColor = "rgba(255, 179, 0, 0.5)";
        ctx.shadowBlur = 8;
        ctx.fillStyle = "#FFB300";
        ctx.strokeStyle = "#FF8F00";
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(sx, sy, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.strokeStyle = "rgba(255, 179, 0, 0.85)";
        ctx.lineWidth = 1.4;
        for (let a = 0; a < 360; a += 45) {
          const rad = (a * Math.PI) / 180;
          ctx.beginPath();
          ctx.moveTo(sx + 8 * Math.cos(rad), sy + 8 * Math.sin(rad));
          ctx.lineTo(sx + 12 * Math.cos(rad), sy + 12 * Math.sin(rad));
          ctx.stroke();
        }
        ctx.restore();

        const [moonLat, moonLon] = calculateSubLunarPoint(now);
        const mx = lonToX(moonLon, W),
          my = latToY(moonLat, H);
        ctx.save();
        ctx.shadowColor = "rgba(224, 224, 224, 0.4)";
        ctx.shadowBlur = 6;
        ctx.fillStyle = "#e0e0e0";
        ctx.strokeStyle = "#9e9e9e";
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.arc(mx, my, 5.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.fillStyle = "rgba(189, 189, 189, 0.5)";
        ctx.beginPath();
        ctx.arc(mx - 1.5, my - 1, 1.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(189, 189, 189, 0.4)";
        ctx.beginPath();
        ctx.arc(mx + 2, my + 1.5, 0.9, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    // === World Map (land visible against ocean) ===
    ctx.fillStyle = COLORS.land;
    ctx.strokeStyle = "rgba(100, 120, 160, 0.25)";
    ctx.lineWidth = 1;

    for (const pathStr of WORLD_MAP_PATHS) {
      const commands = pathStr.split(" ");
      ctx.beginPath();
      for (const cmd of commands) {
        if (cmd.startsWith("M") || cmd.startsWith("L")) {
          const coords = cmd.substring(1).split(",");
          const lon = parseFloat(coords[0]);
          const lat = parseFloat(coords[1]);
          const x = lonToX(lon, W);
          const y = latToY(lat, H);
          if (cmd.startsWith("M")) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        } else if (cmd === "Z") {
          ctx.closePath();
        }
      }
      ctx.fill();
      ctx.stroke();
    }

    // === Grid ===
    if (showGrid) {
      ctx.strokeStyle = COLORS.grid;
      ctx.lineWidth = 0.4;
      for (let lon = -180; lon <= 180; lon += 30) {
        const x = lonToX(lon, W);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
      for (let lat = -90; lat <= 90; lat += 30) {
        const y = latToY(lat, H);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
    }

    // === Equator ===
    if (showEquator) {
      const eqY = latToY(0, H);
      ctx.strokeStyle = COLORS.equator;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(0, eqY);
      ctx.lineTo(W, eqY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // === Ground Station Coverage Circles (latitude-corrected ellipses) ===
    for (const gs of groundStations) {
      const enhanced = gs as GroundStationEnhanced;
      if (enhanced.showCoverage && enhanced.coverageRadius) {
        const statusColor =
          STATUS_COLOR_MAP[enhanced.status || "standby"] ||
          STATUS_COLOR_MAP.standby;

        // Use the coverage radius as-is (in degrees) — caller can compute
        // from visibilityRadiusDeg() or provide a direct value
        drawCoverageEllipse(
          ctx,
          gs.latitude,
          gs.longitude,
          enhanced.coverageRadius,
          W,
          H,
          lonToX,
          latToY,
        );
        ctx.fillStyle = `${statusColor}15`;
        ctx.fill();
        ctx.strokeStyle = `${statusColor}40`;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // === Ground Stations (Astro UX Icons + Status Badges) ===
    for (const gs of groundStations) {
      const enhanced = gs as GroundStationEnhanced;
      const x = lonToX(gs.longitude, W);
      const y = latToY(gs.latitude, H);
      const status = enhanced.status || "standby";
      const statusColor = STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.standby;
      const stationType = enhanced.type || "dish";
      const iconSize = 20;
      const halfIcon = iconSize / 2;

      ctx.save();
      ctx.translate(x, y);

      // -- Background circle (subtle glow) --
      ctx.beginPath();
      ctx.arc(0, 0, halfIcon + 2, 0, Math.PI * 2);
      ctx.fillStyle = `${statusColor}18`;
      ctx.fill();

      // -- Icon background pill --
      ctx.beginPath();
      ctx.arc(0, 0, halfIcon, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
      ctx.fill();
      ctx.strokeStyle = `${statusColor}66`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // -- Draw Astro UX icon via SVG paths scaled to icon size --
      ctx.save();
      const iconScale = iconSize / 24;
      ctx.translate(-halfIcon, -halfIcon);
      ctx.scale(iconScale, iconScale);

      if (stationType === "dish" || stationType === "omni") {
        // Antenna icon (concentric arcs — Astro antenna pattern)
        ctx.strokeStyle = statusColor;
        ctx.fillStyle = statusColor;
        ctx.lineWidth = 2 / iconScale;
        // Outer arc
        ctx.beginPath();
        ctx.arc(12, 12, 10, Math.PI * 1.25, Math.PI * 1.75);
        ctx.stroke();
        // Middle arc
        ctx.beginPath();
        ctx.arc(12, 12, 6.5, Math.PI * 1.2, Math.PI * 1.8);
        ctx.stroke();
        // Inner arc
        ctx.beginPath();
        ctx.arc(12, 12, 3, Math.PI * 1.15, Math.PI * 1.85);
        ctx.stroke();
        // Center dot (feed point)
        ctx.beginPath();
        ctx.arc(12, 12, 1.5, 0, Math.PI * 2);
        ctx.fill();
        // Stand
        ctx.lineWidth = 1.5 / iconScale;
        ctx.beginPath();
        ctx.moveTo(12, 14);
        ctx.lineTo(12, 20);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(8, 20);
        ctx.lineTo(16, 20);
        ctx.stroke();
      } else if (stationType === "phased-array") {
        // Phased array icon (grid pattern)
        ctx.strokeStyle = statusColor;
        ctx.fillStyle = statusColor;
        ctx.lineWidth = 1.5 / iconScale;
        ctx.strokeRect(5, 4, 14, 12);
        // Grid lines
        for (let gx = 8; gx <= 16; gx += 4) {
          ctx.beginPath();
          ctx.moveTo(gx, 4);
          ctx.lineTo(gx, 16);
          ctx.stroke();
        }
        for (let gy = 8; gy <= 12; gy += 4) {
          ctx.beginPath();
          ctx.moveTo(5, gy);
          ctx.lineTo(19, gy);
          ctx.stroke();
        }
        // Stand
        ctx.beginPath();
        ctx.moveTo(12, 16);
        ctx.lineTo(12, 21);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(8, 21);
        ctx.lineTo(16, 21);
        ctx.stroke();
      } else {
        // Relay: satellite dish with signal waves
        ctx.strokeStyle = statusColor;
        ctx.fillStyle = statusColor;
        ctx.lineWidth = 2 / iconScale;
        // Dish bowl
        ctx.beginPath();
        ctx.arc(10, 12, 7, Math.PI * 0.8, Math.PI * 1.7);
        ctx.stroke();
        // Feed arm
        ctx.lineWidth = 1.5 / iconScale;
        ctx.beginPath();
        ctx.moveTo(10, 12);
        ctx.lineTo(16, 6);
        ctx.stroke();
        // Signal waves
        ctx.lineWidth = 1.2 / iconScale;
        ctx.beginPath();
        ctx.arc(17, 5, 2.5, Math.PI * 1.2, Math.PI * 1.7);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(17, 5, 4.5, Math.PI * 1.15, Math.PI * 1.75);
        ctx.stroke();
        // Base
        ctx.lineWidth = 1.5 / iconScale;
        ctx.beginPath();
        ctx.moveTo(10, 14);
        ctx.lineTo(10, 20);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(6, 20);
        ctx.lineTo(14, 20);
        ctx.stroke();
      }

      ctx.restore(); // icon scaling

      // -- Astro UX Status Badge (top-left of icon) --
      const badgeX = -halfIcon + 1;
      const badgeY = -halfIcon + 1;
      const badgeSize = 7;

      // Badge background ring
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeSize / 2 + 2, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(15, 23, 42, 0.9)";
      ctx.fill();

      // Status shape per Astro UX spec (StatusIndicator.tsx canonical reference):
      //   off → small filled dot, standby → ring (hollow circle),
      //   normal → filled circle, caution → square,
      //   serious → diamond, critical → triangle pointing DOWN
      ctx.fillStyle = statusColor;
      ctx.strokeStyle = statusColor;
      ctx.lineWidth = 1;
      if (status === "normal") {
        // Filled circle for normal
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, badgeSize / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (status === "standby") {
        // Ring (hollow circle) for standby
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, badgeSize / 2 - 0.5, 0, Math.PI * 2);
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (status === "caution") {
        // Square for caution
        const hs = badgeSize / 2;
        ctx.fillRect(badgeX - hs, badgeY - hs, badgeSize, badgeSize);
      } else if (status === "serious") {
        // Diamond for serious
        const hs = badgeSize / 2;
        ctx.beginPath();
        ctx.moveTo(badgeX, badgeY - hs);
        ctx.lineTo(badgeX + hs, badgeY);
        ctx.lineTo(badgeX, badgeY + hs);
        ctx.lineTo(badgeX - hs, badgeY);
        ctx.closePath();
        ctx.fill();
      } else if (status === "critical") {
        // Triangle pointing DOWN for critical
        const hs = badgeSize / 2;
        ctx.beginPath();
        ctx.moveTo(badgeX, badgeY + hs); // bottom point
        ctx.lineTo(badgeX + hs, badgeY - hs); // top-right
        ctx.lineTo(badgeX - hs, badgeY - hs); // top-left
        ctx.closePath();
        ctx.fill();
      } else {
        // Off: small filled dot
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, badgeSize / 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      // Label
      ctx.fillStyle = COLORS.text;
      ctx.font = '9px "Inter", system-ui, sans-serif';
      ctx.textAlign = "center";
      ctx.fillText(
        gs.name.length > 14 ? gs.name.slice(0, 14) + "…" : gs.name,
        x,
        y + halfIcon + 12,
      );
    }

    // === Light Sources (visible only on the night side of the terminator) ===
    if (lightSources && lightSources.length > 0 && showTerminator) {
      const now = terminatorTime ?? new Date();

      for (const light of lightSources) {
        const lx = lonToX(light.longitude, W);
        const ly = latToY(light.latitude, H);
        const r = light.radius ?? 4;
        const intensity = light.intensity ?? 0.8;
        const color = light.color ?? "#ffeedd";

        // Compute night factor: how deeply this point is inside the night zone.
        // We use the geometric terminator (0°) and astronomical (18°) as endpoints
        // for a 0→1 ramp.
        const dayOfYear = Math.floor(
          (now.getTime() - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86400000,
        );
        const declination =
          -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
        const decRad = (declination * Math.PI) / 180;
        const lsSubSolarLon =
          -(now.getUTCHours() + now.getUTCMinutes() / 60 - 12) * 15;
        const latRad = (light.latitude * Math.PI) / 180;

        // Solar elevation angle at the light's position
        const sinAlt =
          Math.sin(latRad) * Math.sin(decRad) +
          Math.cos(latRad) *
            Math.cos(decRad) *
            Math.cos(((light.longitude - lsSubSolarLon) * Math.PI) / 180);
        const solarAltDeg = (Math.asin(sinAlt) * 180) / Math.PI;

        // Lights fade in: full day (alt > 0°) = invisible, deep night (alt < -18°) = full brightness
        let nightFactor: number;
        if (solarAltDeg >= 0) nightFactor = 0;
        else if (solarAltDeg <= -18) nightFactor = 1;
        else nightFactor = Math.min(1, -solarAltDeg / 18);

        if (nightFactor < 0.01) continue;

        const alpha = intensity * nightFactor;
        const [cr, cg, cb] = hexToRgb(color);
        const grad = ctx.createRadialGradient(lx, ly, 0, lx, ly, r * 2);
        grad.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${alpha})`);
        grad.addColorStop(0.4, `rgba(${cr}, ${cg}, ${cb}, ${alpha * 0.6})`);
        grad.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(lx, ly, r * 2, 0, Math.PI * 2);
        ctx.fill();

        // Bright core
        ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${Math.min(1, alpha * 1.3)})`;
        ctx.beginPath();
        ctx.arc(lx, ly, Math.max(1, r * 0.4), 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // === Satellite Tracks ===
    allSatellites.forEach((sat, satIdx) => {
      const track = sat.groundTrack;
      if (!track || track.length === 0) return;

      const trackColor =
        sat.color || DEFAULT_TRACK_COLORS[satIdx % DEFAULT_TRACK_COLORS.length];
      const futureIdx = sat.futureTrackIndex ?? track.length;

      // Draw past track (solid)
      ctx.strokeStyle = trackColor;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.setLineDash([]);
      ctx.beginPath();

      let prevX = lonToX(track[0].longitude, W);
      let prevY = latToY(track[0].latitude, H);
      ctx.moveTo(prevX, prevY);

      for (let i = 1; i < Math.min(futureIdx, track.length); i++) {
        const x = lonToX(track[i].longitude, W);
        const y = latToY(track[i].latitude, H);
        const crossesDateLine = Math.abs(x - prevX) > W / 2;
        if (crossesDateLine) {
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        prevX = x;
        prevY = y;
      }
      ctx.stroke();

      // Draw future track (dashed)
      if (futureIdx < track.length) {
        ctx.strokeStyle = `${trackColor}88`;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(
          lonToX(track[futureIdx].longitude, W),
          latToY(track[futureIdx].latitude, H),
        );
        let fpx = lonToX(track[futureIdx].longitude, W);

        for (let i = futureIdx + 1; i < track.length; i++) {
          const x = lonToX(track[i].longitude, W);
          const y = latToY(track[i].latitude, H);
          if (Math.abs(x - fpx) > W / 2) {
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          fpx = x;
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw access segments (contact passes)
      if (sat.accessMask && sat.accessMask.some(Boolean)) {
        ctx.strokeStyle = STATUS_COLOR_MAP.normal;
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.beginPath();
        let drawing = false;
        let apx = lonToX(track[0].longitude, W);

        for (let i = 0; i < track.length; i++) {
          const x = lonToX(track[i].longitude, W);
          const y = latToY(track[i].latitude, H);
          const active = sat.accessMask[i];
          const crossesDateLine = i > 0 && Math.abs(x - apx) > W / 2;

          if (active && !crossesDateLine) {
            if (!drawing) {
              ctx.moveTo(x, y);
              drawing = true;
            } else ctx.lineTo(x, y);
          } else {
            if (drawing) {
              ctx.stroke();
              ctx.beginPath();
              drawing = false;
            }
          }
          apx = x;
        }
        if (drawing) ctx.stroke();
      }

      // AOS/LOS markers
      if (sat.passMarkers) {
        for (const marker of sat.passMarkers) {
          const mx = lonToX(marker.longitude, W);
          const my = latToY(marker.latitude, H);
          const markerColor =
            marker.type === "aos"
              ? STATUS_COLOR_MAP.normal
              : STATUS_COLOR_MAP.critical;

          ctx.save();
          ctx.translate(mx, my);
          // Triangle marker
          ctx.beginPath();
          if (marker.type === "aos") {
            ctx.moveTo(0, -6);
            ctx.lineTo(4, 2);
            ctx.lineTo(-4, 2);
          } else {
            ctx.moveTo(0, 6);
            ctx.lineTo(4, -2);
            ctx.lineTo(-4, -2);
          }
          ctx.closePath();
          ctx.fillStyle = markerColor;
          ctx.fill();
          ctx.restore();

          if (marker.label) {
            ctx.fillStyle = markerColor;
            ctx.font = '8px "Inter", system-ui, sans-serif';
            ctx.textAlign = "center";
            ctx.fillText(
              marker.label,
              mx,
              marker.type === "aos" ? my - 9 : my + 13,
            );
          }
        }
      }

      // Satellite footprint (latitude-corrected ellipse)
      if (sat.showFootprint && sat.footprintRadius) {
        const lastPt =
          futureIdx > 0
            ? track[Math.min(futureIdx - 1, track.length - 1)]
            : track[track.length - 1];

        drawCoverageEllipse(
          ctx,
          lastPt.latitude,
          lastPt.longitude,
          sat.footprintRadius,
          W,
          H,
          lonToX,
          latToY,
        );
        ctx.fillStyle = `${trackColor}12`;
        ctx.fill();
        ctx.strokeStyle = `${trackColor}50`;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Current position satellite icon (Astro UX style)
      const currentIdx =
        futureIdx > 0
          ? Math.min(futureIdx - 1, track.length - 1)
          : track.length - 1;
      const current = track[currentIdx];
      const cx = lonToX(current.longitude, W);
      const cy = latToY(current.latitude, H);
      const satStatus = sat.status || "normal";
      const statusColor =
        STATUS_COLOR_MAP[satStatus] || STATUS_COLOR_MAP.normal;
      const satIconSize = 22;
      const satHalf = satIconSize / 2;

      // Outer pulsing glow
      ctx.beginPath();
      ctx.arc(cx, cy, satHalf + 6, 0, Math.PI * 2);
      ctx.fillStyle = `${statusColor}12`;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, satHalf + 3, 0, Math.PI * 2);
      ctx.fillStyle = `${statusColor}20`;
      ctx.fill();

      // Icon background
      ctx.save();
      ctx.translate(cx, cy);

      ctx.beginPath();
      ctx.arc(0, 0, satHalf, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(15, 23, 42, 0.88)";
      ctx.fill();
      ctx.strokeStyle = `${statusColor}88`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Draw satellite shape (body + solar panels)
      ctx.fillStyle = statusColor;
      // Body (rounded rect approximation)
      const bw = 5,
        bh = 3.5;
      ctx.beginPath();
      ctx.moveTo(-bw / 2 + 0.5, -bh / 2);
      ctx.lineTo(bw / 2 - 0.5, -bh / 2);
      ctx.arcTo(bw / 2, -bh / 2, bw / 2, -bh / 2 + 0.5, 0.5);
      ctx.lineTo(bw / 2, bh / 2 - 0.5);
      ctx.arcTo(bw / 2, bh / 2, bw / 2 - 0.5, bh / 2, 0.5);
      ctx.lineTo(-bw / 2 + 0.5, bh / 2);
      ctx.arcTo(-bw / 2, bh / 2, -bw / 2, bh / 2 - 0.5, 0.5);
      ctx.lineTo(-bw / 2, -bh / 2 + 0.5);
      ctx.arcTo(-bw / 2, -bh / 2, -bw / 2 + 0.5, -bh / 2, 0.5);
      ctx.closePath();
      ctx.fill();

      // Solar panel left
      ctx.fillStyle = `${statusColor}BB`;
      ctx.fillRect(-satHalf + 2, -2, 5, 4);
      // Panel lines
      ctx.strokeStyle = `${statusColor}55`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(-satHalf + 4.5, -2);
      ctx.lineTo(-satHalf + 4.5, 2);
      ctx.stroke();

      // Solar panel right
      ctx.fillStyle = `${statusColor}BB`;
      ctx.fillRect(satHalf - 7, -2, 5, 4);
      ctx.strokeStyle = `${statusColor}55`;
      ctx.beginPath();
      ctx.moveTo(satHalf - 4.5, -2);
      ctx.lineTo(satHalf - 4.5, 2);
      ctx.stroke();

      // Panel connectors
      ctx.strokeStyle = statusColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-bw / 2, 0);
      ctx.lineTo(-satHalf + 7, 0);
      ctx.moveTo(bw / 2, 0);
      ctx.lineTo(satHalf - 7, 0);
      ctx.stroke();

      // -- Astro UX Status Badge (top-left of satellite icon) --
      // Shapes per canonical StatusIndicator.tsx:
      //   off → small dot, standby → ring, normal → filled circle,
      //   caution → square, serious → diamond, critical → triangle DOWN
      const satBadgeX = -satHalf + 2;
      const satBadgeY = -satHalf + 2;
      const satBadgeSize = 7;

      // Badge background
      ctx.beginPath();
      ctx.arc(satBadgeX, satBadgeY, satBadgeSize / 2 + 2, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(15, 23, 42, 0.9)";
      ctx.fill();

      ctx.fillStyle = statusColor;
      ctx.strokeStyle = statusColor;
      ctx.lineWidth = 1;
      if (satStatus === "normal") {
        // Filled circle
        ctx.beginPath();
        ctx.arc(satBadgeX, satBadgeY, satBadgeSize / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (satStatus === "standby") {
        // Ring (hollow circle)
        ctx.beginPath();
        ctx.arc(satBadgeX, satBadgeY, satBadgeSize / 2 - 0.5, 0, Math.PI * 2);
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (satStatus === "caution") {
        const hs = satBadgeSize / 2;
        ctx.fillRect(
          satBadgeX - hs,
          satBadgeY - hs,
          satBadgeSize,
          satBadgeSize,
        );
      } else if (satStatus === "serious") {
        // Diamond
        const hs = satBadgeSize / 2;
        ctx.beginPath();
        ctx.moveTo(satBadgeX, satBadgeY - hs);
        ctx.lineTo(satBadgeX + hs, satBadgeY);
        ctx.lineTo(satBadgeX, satBadgeY + hs);
        ctx.lineTo(satBadgeX - hs, satBadgeY);
        ctx.closePath();
        ctx.fill();
      } else if (satStatus === "critical") {
        // Triangle pointing DOWN
        const hs = satBadgeSize / 2;
        ctx.beginPath();
        ctx.moveTo(satBadgeX, satBadgeY + hs); // bottom point
        ctx.lineTo(satBadgeX + hs, satBadgeY - hs); // top-right
        ctx.lineTo(satBadgeX - hs, satBadgeY - hs); // top-left
        ctx.closePath();
        ctx.fill();
      } else {
        // Off: small filled dot
        ctx.beginPath();
        ctx.arc(satBadgeX, satBadgeY, satBadgeSize / 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      // Satellite label
      ctx.fillStyle = COLORS.textPrimary;
      ctx.font = 'bold 10px "Inter", system-ui, sans-serif';
      ctx.textAlign = "center";
      ctx.fillText(sat.name, cx, cy - satHalf - 6);
    });

    // === Legend ===
    if (showLegend && (allSatellites.length > 0 || groundStations.length > 0)) {
      const legendX = 10;
      const legendY = 10;
      const lineHeight = 14;
      const items: Array<{ color: string; label: string; dash?: boolean }> = [];

      allSatellites.forEach((sat, idx) => {
        const c =
          sat.color || DEFAULT_TRACK_COLORS[idx % DEFAULT_TRACK_COLORS.length];
        items.push({ color: c, label: sat.name });
      });
      if (groundStations.length > 0) {
        items.push({
          color: STATUS_COLOR_MAP.standby,
          label: `${groundStations.length} Ground Station${groundStations.length > 1 ? "s" : ""}`,
        });
      }

      const maxLabelWidth =
        Math.max(...items.map((item) => ctx.measureText(item.label).width)) +
        28;
      const legendH = items.length * lineHeight + 10;

      ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
      ctx.strokeStyle = COLORS.borderMuted;
      ctx.lineWidth = 1;
      const r = 4;
      ctx.beginPath();
      ctx.moveTo(legendX + r, legendY);
      ctx.lineTo(legendX + maxLabelWidth - r, legendY);
      ctx.arcTo(
        legendX + maxLabelWidth,
        legendY,
        legendX + maxLabelWidth,
        legendY + r,
        r,
      );
      ctx.lineTo(legendX + maxLabelWidth, legendY + legendH - r);
      ctx.arcTo(
        legendX + maxLabelWidth,
        legendY + legendH,
        legendX + maxLabelWidth - r,
        legendY + legendH,
        r,
      );
      ctx.lineTo(legendX + r, legendY + legendH);
      ctx.arcTo(legendX, legendY + legendH, legendX, legendY + legendH - r, r);
      ctx.lineTo(legendX, legendY + r);
      ctx.arcTo(legendX, legendY, legendX + r, legendY, r);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      items.forEach((item, i) => {
        const iy = legendY + 8 + i * lineHeight;
        // Color swatch
        ctx.fillStyle = item.color;
        ctx.beginPath();
        ctx.arc(legendX + 10, iy + 3, 4, 0, Math.PI * 2);
        ctx.fill();
        // Label
        ctx.fillStyle = COLORS.textPrimary;
        ctx.font = '10px "Inter", system-ui, sans-serif';
        ctx.textAlign = "left";
        ctx.fillText(item.label, legendX + 20, iy + 7);
      });
    }

    // === Pins (read-only on canvas backend) ===
    if (pins && pins.length > 0) {
      pins.forEach((pin) => {
        const px = lonToX(pin.longitude, W);
        const py = latToY(pin.latitude, H);
        const pinColor = pin.color || tokens.colors.accent.primary;

        // Drop shadow
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;

        // Pin body: inverted teardrop shape
        ctx.beginPath();
        ctx.arc(px, py - 10, 6, Math.PI, 0);
        ctx.lineTo(px, py);
        ctx.closePath();
        ctx.fillStyle = pinColor;
        ctx.fill();
        ctx.restore();

        // Inner dot
        ctx.beginPath();
        ctx.arc(px, py - 10, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "#0d1323";
        ctx.fill();

        // Label
        if (pin.label) {
          ctx.fillStyle = COLORS.textPrimary;
          ctx.font = '10px "Inter", system-ui, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText(pin.label, px, py - 20);
        }
      });
    }

    // === No Data Message ===
    if (
      allSatellites.length === 0 &&
      groundStations.length === 0 &&
      (!pins || pins.length === 0)
    ) {
      ctx.fillStyle = COLORS.text;
      ctx.font = '12px "Inter", system-ui, sans-serif';
      ctx.textAlign = "center";
      ctx.fillText(emptyMessage, W / 2, H / 2);
    }
  }, [
    allSatellites,
    groundStations,
    height,
    showTerminator,
    terminatorTime,
    showGrid,
    showLegend,
    showEquator,
    lonToX,
    latToY,
    COLORS,
    emptyMessage,
    pins,
    lightSources,
    tokens.colors.accent.primary,
  ]);

  // Tooltip on mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const W = rect.width;
      const H = typeof height === "number" ? height : rect.height;

      // Check satellite positions
      for (const sat of allSatellites) {
        if (!sat.groundTrack.length) continue;
        const futureIdx = sat.futureTrackIndex ?? sat.groundTrack.length;
        const currentIdx =
          futureIdx > 0
            ? Math.min(futureIdx - 1, sat.groundTrack.length - 1)
            : sat.groundTrack.length - 1;
        const lastPt = sat.groundTrack[currentIdx];
        const sx = lonToX(lastPt.longitude, W);
        const sy = latToY(lastPt.latitude, H);
        const dist = Math.sqrt((mx - sx) ** 2 + (my - sy) ** 2);
        if (dist < 18) {
          const satStatus = sat.status || "normal";
          setTooltip({
            x: e.clientX - rect.left + 15,
            y: e.clientY - rect.top - 10,
            type: "satellite",
            name: sat.name,
            status: satStatus,
            statusColor: STATUS_COLOR_MAP[satStatus] || STATUS_COLOR_MAP.normal,
            lat: lastPt.latitude,
            lon: lastPt.longitude,
            alt: lastPt.altitude,
            trackColor:
              sat.color ||
              DEFAULT_TRACK_COLORS[
                allSatellites.indexOf(sat) % DEFAULT_TRACK_COLORS.length
              ],
            hasFootprint: sat.showFootprint,
            futurePoints: sat.groundTrack.length - futureIdx,
            pastPoints: futureIdx,
          });
          return;
        }
      }

      // Check ground stations
      for (const gs of groundStations) {
        const gx = lonToX(gs.longitude, W);
        const gy = latToY(gs.latitude, H);
        const dist = Math.sqrt((mx - gx) ** 2 + (my - gy) ** 2);
        if (dist < 16) {
          const enhanced = gs as GroundStationEnhanced;
          const gsStatus = enhanced.status || "standby";
          setTooltip({
            x: e.clientX - rect.left + 15,
            y: e.clientY - rect.top - 10,
            type: "station",
            name: gs.name,
            status: gsStatus,
            statusColor: STATUS_COLOR_MAP[gsStatus] || STATUS_COLOR_MAP.standby,
            lat: gs.latitude,
            lon: gs.longitude,
            stationType: enhanced.type || "dish",
            network:
              "network" in gs && gs.network ? String(gs.network) : undefined,
            coverageRadius: enhanced.coverageRadius,
          });
          return;
        }
      }

      setTooltip(null);
    },
    [allSatellites, groundStations, height, lonToX, latToY],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const W = rect.width;
      const H = typeof height === "number" ? height : rect.height;

      for (const sat of allSatellites) {
        if (!sat.groundTrack.length) continue;
        const futureIdx = sat.futureTrackIndex ?? sat.groundTrack.length;
        const currentIdx =
          futureIdx > 0
            ? Math.min(futureIdx - 1, sat.groundTrack.length - 1)
            : sat.groundTrack.length - 1;
        const currentPt = sat.groundTrack[currentIdx];
        const sx = lonToX(currentPt.longitude, W);
        const sy = latToY(currentPt.latitude, H);
        if (Math.sqrt((mx - sx) ** 2 + (my - sy) ** 2) < 15) {
          onSatelliteClick?.(sat.id);
          return;
        }
      }

      for (const gs of groundStations) {
        const gx = lonToX(gs.longitude, W);
        const gy = latToY(gs.latitude, H);
        if (Math.sqrt((mx - gx) ** 2 + (my - gy) ** 2) < 12) {
          onStationClick?.("id" in gs ? gs.id : gs.name);
          return;
        }
      }
    },
    [
      allSatellites,
      groundStations,
      height,
      lonToX,
      latToY,
      onSatelliteClick,
      onStationClick,
    ],
  );

  useEffect(() => {
    draw();
    window.addEventListener("resize", draw);
    return () => window.removeEventListener("resize", draw);
  }, [draw]);

  // Leaflet backend: return after all hooks so hook count is stable
  const resolvedMinHeightForLeaflet =
    minHeight || (typeof height === "number" ? `${height}px` : "400px");

  if (mapProvider === "leaflet") {
    if (LeafletMap) {
      return (
        <div
          role="img"
          aria-label={
            allSatellites.length > 0 || groundStations.length > 0
              ? "Ground track map showing satellite tracks and ground stations"
              : "Ground track map"
          }
          style={{
            display: "block",
            width: width ?? "100%",
            height: typeof height === "string" ? height : undefined,
            minHeight: resolvedMinHeightForLeaflet,
          }}
        >
          <LeafletMap
            allSatellites={allSatellites}
            groundStations={groundStations}
            showTerminator={showTerminator}
            terminatorTime={terminatorTime}
            showGrid={showGrid}
            showLegend={showLegend}
            showEquator={showEquator}
            showRecenterButton={showRecenterButton}
            showMapStyleToggle={showMapStyleToggle}
            defaultCenter={leafletDefaultCenter}
            defaultZoom={leafletDefaultZoom}
            height={height}
            width={width}
            minHeight={minHeight}
            emptyMessage={emptyMessage}
            tileUrl={tileUrl}
            nightTileUrl={nightTileUrl}
            lightSources={lightSources}
            className={className}
            onSatelliteClick={onSatelliteClick}
            onStationClick={onStationClick}
            pins={pins}
            pinsEditable={pinsEditable}
            onPinAdd={onPinAdd}
            onPinUpdate={onPinUpdate}
            onPinRemove={onPinRemove}
            customLayers={customLayers}
            onLayerChange={onLayerChange}
            showCelestialMarkers={showCelestialMarkers}
          />
        </div>
      );
    }
    if (!leafletFailed) {
      return (
        <div
          className={`zendir-ground-track-map ${className}`}
          style={{
            width,
            height: typeof height === "string" ? height : undefined,
            minHeight: resolvedMinHeightForLeaflet,
            backgroundColor: tokens.colors.background.base,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: tokens.colors.text.secondary, fontSize: 14 }}>
            Loading map…
          </span>
        </div>
      );
    }
  }

  const resolvedMinHeight =
    minHeight || (typeof height === "number" ? `${height}px` : "400px");

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`zendir-ground-track-map ${className}`}
        style={{
          width,
          minHeight: resolvedMinHeight,
          backgroundColor: COLORS.background,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{ color: COLORS.text, fontSize: "14px", letterSpacing: "1px" }}
        >
          Loading…
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={
        allSatellites.length > 0 || groundStations.length > 0
          ? "Ground track map showing satellite tracks and ground stations"
          : "Ground track map"
      }
      className={`zendir-ground-track-map ${className}`}
      style={{
        width,
        height: typeof height === "string" ? height : undefined,
        minHeight: resolvedMinHeight,
        backgroundColor: COLORS.background,
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
      }}
      data-map-backend="canvas"
    >
      {/* Hint when Leaflet was requested but failed (e.g. leaflet not installed) */}
      {mapProvider === "leaflet" && leafletFailed && (
        <div
          style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            zIndex: 100,
            padding: "4px 8px",
            background: "rgba(0,0,0,0.75)",
            color: tokens.colors.status.caution,
            fontSize: 11,
            borderRadius: 4,
          }}
        >
          Static view (install{" "}
          <code style={{ fontFamily: "monospace" }}>leaflet</code> for zoom/pan)
        </div>
      )}
      {/* Recenter button (SRO compat) */}
      {showRecenterButton && (
        <button
          type="button"
          onClick={handleRecenter}
          title="Recenter Map"
          aria-label="Recenter map"
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 20,
            background: tokens.colors.background.elevated,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: "4px",
            color: tokens.colors.text.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "12px",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
          </svg>
        </button>
      )}

      <canvas
        ref={canvasRef}
        style={{ display: "block", cursor: tooltip ? "pointer" : "default" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onClick={handleClick}
      />
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            background: tokens.colors.background.surface,
            backdropFilter: "blur(8px)",
            border: `1px solid ${tooltip.statusColor}40`,
            borderRadius: "8px",
            padding: 0,
            pointerEvents: "none",
            zIndex: 10,
            color: tokens.colors.text.primary,
            fontSize: "11px",
            fontFamily: '"Inter", system-ui, sans-serif',
            lineHeight: "1.5",
            boxShadow: `0 4px 16px rgba(0, 0, 0, 0.5), 0 0 12px ${tooltip.statusColor}15`,
            minWidth: 180,
            overflow: "hidden",
          }}
        >
          {/* Header with status accent */}
          <div
            style={{
              padding: "6px 10px",
              borderBottom: `1px solid ${tooltip.statusColor}25`,
              background: `${tooltip.statusColor}10`,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: tooltip.status === "caution" ? 1 : "50%",
                background: tooltip.statusColor,
                display: "inline-block",
                flexShrink: 0,
                boxShadow: `0 0 4px ${tooltip.statusColor}88`,
              }}
            />
            <span style={{ fontWeight: 600, fontSize: 12 }}>
              {tooltip.name}
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 9,
                fontWeight: 600,
                textTransform: "uppercase",
                color: tooltip.statusColor,
                letterSpacing: "0.5px",
              }}
            >
              {tooltip.status}
            </span>
          </div>
          {/* Details */}
          <div
            style={{
              padding: "6px 10px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Type badge */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 2,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "1px 5px",
                  borderRadius: 3,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  background:
                    tooltip.type === "satellite"
                      ? `${tokens.colors.status.standby}26`
                      : `${tokens.colors.status.normal}26`,
                  color:
                    tooltip.type === "satellite"
                      ? tokens.colors.status.standby
                      : tokens.colors.status.normal,
                }}
              >
                {tooltip.type === "satellite" ? (
                  <>
                    <AstroIcon
                      name="satellite"
                      size="extra-small"
                      label="Satellite"
                      style={{ marginRight: 4, verticalAlign: "middle" }}
                    />
                    Satellite
                  </>
                ) : (
                  <>
                    <AstroIcon
                      name="antenna"
                      size="extra-small"
                      label={tooltip.stationType || "Ground station"}
                      style={{ marginRight: 4, verticalAlign: "middle" }}
                    />
                    {tooltip.stationType || "dish"}
                  </>
                )}
              </span>
              {tooltip.network && (
                <span
                  style={{
                    fontSize: 9,
                    padding: "1px 5px",
                    borderRadius: 3,
                    background: "rgba(157, 112, 255, 0.15)",
                    color: "#9D70FF",
                  }}
                >
                  {tooltip.network}
                </span>
              )}
            </div>
            {/* Coordinates */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "52px 1fr",
                gap: "2px 8px",
                fontSize: 10,
              }}
            >
              <span style={{ color: tokens.colors.text.muted }}>Lat</span>
              <span
                style={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontWeight: 500,
                }}
              >
                {tooltip.lat >= 0 ? "+" : ""}
                {tooltip.lat.toFixed(4)}°
              </span>
              <span style={{ color: tokens.colors.text.muted }}>Lon</span>
              <span
                style={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontWeight: 500,
                }}
              >
                {tooltip.lon >= 0 ? "+" : ""}
                {tooltip.lon.toFixed(4)}°
              </span>
              {tooltip.alt !== undefined && (
                <>
                  <span style={{ color: tokens.colors.text.muted }}>
                    Altitude
                  </span>
                  <span
                    style={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontWeight: 500,
                    }}
                  >
                    {tooltip.alt.toFixed(1)} km
                  </span>
                </>
              )}
              {tooltip.coverageRadius !== undefined && (
                <>
                  <span style={{ color: tokens.colors.text.muted }}>
                    Coverage
                  </span>
                  <span
                    style={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontWeight: 500,
                    }}
                  >
                    {tooltip.coverageRadius.toFixed(1)}° radius
                  </span>
                </>
              )}
            </div>
            {/* Satellite extras */}
            {tooltip.type === "satellite" &&
              (tooltip.pastPoints !== undefined ||
                tooltip.futurePoints !== undefined) && (
                <div
                  style={{
                    marginTop: 3,
                    paddingTop: 3,
                    borderTop: `1px solid ${tokens.colors.border.muted}`,
                    display: "flex",
                    gap: 10,
                    fontSize: 9,
                  }}
                >
                  {tooltip.pastPoints !== undefined && (
                    <span>
                      <span style={{ color: tooltip.trackColor }}>━</span>{" "}
                      {tooltip.pastPoints} past pts
                    </span>
                  )}
                  {tooltip.futurePoints !== undefined &&
                    tooltip.futurePoints > 0 && (
                      <span>
                        <span style={{ color: `${tooltip.trackColor}88` }}>
                          ╌
                        </span>{" "}
                        {tooltip.futurePoints} predicted
                      </span>
                    )}
                  {tooltip.hasFootprint && (
                    <span style={{ color: "#9D70FF" }}>◎ Footprint</span>
                  )}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GroundTrackMap;
